import React, { useState, useEffect, useContext, useRef } from "react";
import { TbUser } from "react-icons/tb";
import candidateApi from "../../services/candidateApi";
import jobApi from "../../services/jobApi";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { FunctionalityNames } from "../../enum/enum";
import { getPermissionRights } from "../../utils/common";
import ownIcon from '../../assets/images/icon.png';

import { PiBriefcaseLight, PiChairLight } from "react-icons/pi";
import { MdOutlineMoreHoriz } from "react-icons/md";
export const AddProfileGridView = ({
  searchText,
  setSearchText,
  detailsPopup,
  jobID = 0,
  reloadData,
  reloadCandidateData,
  candidateStatus,
  locationSearch,
  technologySearch,
  searchValue
}) => {
  const { rolePermissions, countryFilterValue, yearFilterValue } = useContext(AuthContext);
  const [candidateListPerm, setCandidateListPerm] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(8);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const rowRefs = useRef([]);

  // Fetch candidates
  const fetchData = async (page, searchTextParam) => {
    try {
      setLoading(true);
      let queryString = `?pageIndex=${page}&pageLimit=${rowsPerPage}`;
      if (searchTextParam) queryString += `&searchText=${encodeURIComponent(searchTextParam)}`;
      if (yearFilterValue && yearFilterValue > 0) queryString += `&year=${yearFilterValue}`;
      if (countryFilterValue && countryFilterValue > 0) queryString += `&country=${countryFilterValue}`;
      if (jobID > 0) queryString += `&jobId=${jobID}`;
      if (locationSearch) {
        queryString += `&locationSearch=${locationSearch}`;
      }
      if (technologySearch) {
        queryString += `&technologySearch=${technologySearch}`;
      }
      if (searchValue) {
        queryString += `&searchValue=${searchValue}`;
      }

      const response = await candidateApi.getAddProfileCandidateDetails(queryString);
      let fetchedCandidates = response?.data || [];
      if (candidateStatus) {
        fetchedCandidates = fetchedCandidates.filter(
          (candidate) => candidate.candidateStatus === candidateStatus
        );
      }
      setTotalRecords(fetchedCandidates[0]?.totalRecords || 0);
      if (page === 0) {
        setCandidates(fetchedCandidates);
      } else {
        setCandidates((prev) => [...prev, ...fetchedCandidates]);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  // Reset candidates and page when filters/search change
  useEffect(() => {
    setCurrentPage(0);
    setCandidates([]);
    setFocusedIndex(-1);
  }, [searchText, yearFilterValue, countryFilterValue, reloadCandidateData]);

  // Fetch data when page or filters change
  useEffect(() => {
    fetchData(currentPage, searchText);
    // eslint-disable-next-line
  }, [currentPage, searchText, yearFilterValue, countryFilterValue, reloadCandidateData, locationSearch, technologySearch, searchValue]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100 &&
        !loading &&
        candidates.length < totalRecords
      ) {
        setCurrentPage((prev) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [candidates, loading, totalRecords]);

  // Keyboard navigation (up/down arrow)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (candidates.length === 0) return;
      if (e.key === "ArrowDown") {
        setFocusedIndex((prev) => {
          const next = prev + 1;
          // If at end, load more if available
          if (next >= candidates.length && candidates.length < totalRecords && !loading) {
            setCurrentPage((p) => p + 1);
            return prev; // Wait for more data
          }
          return next < candidates.length ? next : prev;
        });
      } else if (e.key === "ArrowUp") {
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [candidates, totalRecords, loading]);

  // Focus row on index change
  useEffect(() => {
    if (focusedIndex >= 0 && rowRefs.current[focusedIndex]) {
      rowRefs.current[focusedIndex].scrollIntoView({ behavior: "smooth", block: "center" });
      rowRefs.current[focusedIndex].focus();
    }
  }, [focusedIndex, candidates]);

  // Remove candidate from list after mapping
  const handleMapping = async (candidateId) => {
    const requestData = {
      jobId: jobID,
      candidateId: candidateId,
      newBoardId: 1,
      oldBoardId: null,
      newBoardId: 1
    };
    let response = await jobApi.updateBoardMapping(requestData);
    if (response?.data?.ok) {
      setCandidates((prev) => prev.filter((c) => c.id !== candidateId));
      reloadData();
    }
  };

  const navigate = useNavigate();
  const goToCandidateDetails = (id) => {
    navigate(`/candidate/candidate-details/${id}`);
  };

  useEffect(() => {
    if (rolePermissions) {
      setCandidateListPerm(getPermissionRights(rolePermissions, FunctionalityNames.CANDIDATE));
    }
  }, [rolePermissions]);

  return (
    <div className="grid-wrapper">
      {loading && <p>Loading candidates...</p>}
      <div className="row">
        {candidates.length > 0 ? (
          candidates.map((candidate, index) => (
            <div
              className={`col-12 col-md-3 mb-4 grid-holder${focusedIndex === index ? " focused" : ""}`}
              key={candidate.id || index}
              tabIndex={0}
              ref={el => (rowRefs.current[index] = el)}
              style={focusedIndex === index ? { outline: "2px solid #007bff" } : {}}
            >
              <div className="card overflow-hidden p-3 position-relative h-100">
                <div className="d-flex gap-2 align-items-center mb-3 top-sec">
                  <div className="status secondary">{candidate.activitystatus}</div>
                  <div className="ms-auto">
                    <div className="d-flex gap-1 align-items-center badge-holder">
                      {/* candidate tag start here */}
                      {/* <span className="featured"><FaStar /></span>
          <span className="featured blacklist"><MdBlockFlipped /></span> */}
                      <span className="featured own">
                        <img
                          src={ownIcon}
                          alt="Profile"
                        />
                      </span>
                      <span className="featured in-bench" title={"In Bench"}><PiChairLight /> </span>
                      <span className="featured other" title={"Other"}><MdOutlineMoreHoriz /> </span>
                    </div>
                    {/* candidate tag ends here */}
                  </div>
                </div>
                <div className="card-header">
                  <div className="d-flex gap-2 mb-2">
                    <div className="dp-holder position-relative d-flex align-items-center">
                      <span><TbUser /></span>
                    </div>
                    <div className="right-info">
                      <h6>{candidate.firstName} {candidate.lastName}</h6>
                      <p className="mb-0">{candidate.currentDesignation}</p>
                    </div>
                  </div>
                  <div className="d-flex gap-1 skill-holder">
                    {candidate.skillName && candidate.skillName.length > 0 ? (
                      candidate.skillName.map((skillName, idx) => (
                        <span key={idx}>{skillName}</span>
                      ))
                    ) : (
                      <p className="mb-0">No skills available</p>
                    )}
                  </div>
                </div>
                <div className="d-flex row-repeat py-2 align-items-center">
                  <div className="position-relative d-flex align-items-center w-100">
                    <label className="form-label mb-0">Mobile No.</label>
                    <h6 className="ms-auto">{candidate.mobile}</h6>
                  </div>
                </div>
                <div className="d-flex row-repeat py-2 align-items-center">
                  <div className="position-relative d-flex align-items-center w-100">
                    <label className="form-label mb-0">Email ID</label>
                    <h6 className="ms-auto">{candidate.email}</h6>
                  </div>
                </div>
                <div className="d-flex row-repeat py-2 align-items-center">
                  <div className="position-relative d-flex align-items-center w-100">
                    <label className="form-label mb-0">Experience</label>
                    <h6 className="ms-auto">
                      {`${candidate.experienceInYears || 0}.${candidate.experienceInMonths || 0}`} Yrs
                    </h6>
                  </div>
                </div>
                <div className="d-flex row-repeat py-2 align-items-center">
                  <div className="position-relative d-flex align-items-center w-100">
                    <label className="form-label mb-0">Mapped to</label>
                    <h6 className="ms-auto">
                      {Array.isArray(candidate.candidateJob) && candidate.candidateJob.length > 0
                        ? candidate.candidateJob.join(", ")
                        : "Not Yet"}
                    </h6>
                  </div>
                </div>
                {candidateListPerm?.view === 1 && (
                  <div className="d-flex mt-3">
                    {jobID <= 0 ? (
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => goToCandidateDetails(candidate.id)}
                      >
                        Details
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => detailsPopup(candidate.id)}
                      >
                        Details
                      </button>
                    )}
                    <button
                      className="btn btn-outline-secondary ms-auto"
                      disabled={jobID <= 0}
                      onClick={() => handleMapping(candidate.id)}
                    >
                      Select
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-12 text-center">
            <h5>No candidates found</h5>
          </div>
        )}
      </div>
    </div>
  );
};